import { useEffect } from 'react'
import { useAnalyticsView } from '@/features/amplitude'
import {
  Accordion,
  Card,
  Icon,
  Image,
  Link,
  Text,
  RadioGroup,
  RadioGroupItem,
  CardColors,
} from '@/ui'
import {
  faArrowRight,
  faSquareUpRight,
} from '@fortawesome/pro-regular-svg-icons'
import { TypographyStyles } from '@/styles'
interface ISetupBankAccountPreScreenCardProps {
  hasBusinessCheckingAccount: boolean | undefined
  setHasBusinessCheckingAccount: (hasBusinessCheckingAccount: boolean) => void
}

export const SetupBankAccountPreScreenCard = ({
  hasBusinessCheckingAccount,
  setHasBusinessCheckingAccount,
}: ISetupBankAccountPreScreenCardProps) => {
  const pageView = useAnalyticsView()

  useEffect(() => {
    pageView('bank account prescreen')
  }, [pageView])

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <Image
        src="https://heard-images.s3.amazonaws.com/assets/pavilion_with_green_background.png"
        alt="pavilion"
        className="w-full lg:w-1/3"
      />
      <div className="w-full lg:w-2/3 space-y-6">
        <div className="space-y-3">
          <Text variant={TypographyStyles.H3}>
            Do you have a checking account used only for your business?
          </Text>
          <Text>
            If you have personal and business transactions in the same account,
            select <b>No</b>.
          </Text>
        </div>

        <div className="space-y-2">
          <RadioGroup>
            <RadioGroup
              value={
                hasBusinessCheckingAccount
                  ? 'yes'
                  : hasBusinessCheckingAccount === false
                    ? 'no'
                    : undefined
              }
              onValueChange={(val) =>
                setHasBusinessCheckingAccount(val === 'yes')
              }
            >
              <RadioGroupItem value="yes" label="Yes" />
              <RadioGroupItem value="no" label="No" />
            </RadioGroup>
          </RadioGroup>
        </div>

        {hasBusinessCheckingAccount && (
          <Card color={CardColors.LEVEL_1}>
            <Text>Great. You can connect it in the next step.</Text>
          </Card>
        )}

        {hasBusinessCheckingAccount === false && (
          <Card color={CardColors.LEVEL_1} className="space-y-2">
            <Text>
              <b>No problem.</b>
            </Text>
            <Text>
              Heard requires a checking account used only for your business. We
              recommend opening a new one with any bank or institution, then
              coming back to connect it.
            </Text>
            <Link
              href="https://support.joinheard.com/hc/en-us/articles/23407015266967-Using-a-dedicated-business-bank-account-for-your-practice#open_business_account"
              newPage
            >
              Where can I open a business checking account?
              <Icon icon={faArrowRight} className="ml-2" />
            </Link>
          </Card>
        )}

        <div className="border-t border-border my-4" />

        <div className="space-y-4">
          <Accordion type="single" collapsible variant="text">
            <Accordion.Item value="why-business-only">
              <Accordion.Trigger>
                Why does my account need to be only for business?
              </Accordion.Trigger>
              <Accordion.Content>
                <Text>
                  It&apos;s important to keep your business-related transactions
                  separate from your personal transactions, so we can give you
                  accurate tax estimates, reports, and helpful insights.
                  <br />
                  <br />
                  You should deposit all practice income and pay all business
                  expenses from your business account. This saves time, avoids
                  bookkeeping errors, and protects you and your personal assets
                  in the long run.
                </Text>
              </Accordion.Content>
            </Accordion.Item>

            <Accordion.Item value="no-business-account">
              <Accordion.Trigger>
                What if I don&apos;t have a business account at all?
              </Accordion.Trigger>
              <Accordion.Content>
                <Text>
                  You can open a business checking account with any bank or
                  provider, then come back when you&apos;re ready. We recommend
                  going with a larger institution. They&apos;re more compatible
                  with Heard&apos;s features, including automatic monthly
                  statement uploads.
                  <br />
                  <br />
                  <Text>Some popular banks with Heard customers:</Text>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    {[
                      {
                        name: 'Bluevine',
                        url: 'https://www.bluevine.com/partner/heard-checking',
                      },
                      {
                        name: 'Bank of America',
                        url: 'https://www.bankofamerica.com/smallbusiness/deposits/business-advantage-banking/',
                      },
                      {
                        name: 'BECU',
                        url: 'https://www.becu.org/business-banking/checking',
                      },
                      {
                        name: 'Chase',
                        url: 'https://www.chase.com/business/banking/checking',
                      },
                      {
                        name: 'TD Bank',
                        url: 'https://www.td.com/us/en/small-business/checking-accounts',
                      },
                      {
                        name: 'Wells Fargo',
                        url: 'https://www.wellsfargo.com/biz/checking/',
                      },
                    ].map((bank) => (
                      <li key={bank.name}>
                        <Link variant="secondary" href={bank.url} newPage>
                          {bank.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    href="https://support.joinheard.com/hc/en-us/articles/23407015266967-Using-a-dedicated-business-bank-account-for-your-practice#open_business_account"
                    newPage
                    className="mt-4 inline-block"
                  >
                    Learn more
                    <Icon icon={faSquareUpRight} className="ml-1" />
                  </Link>
                </Text>
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>
        </div>
      </div>
    </div>
  )
}
